import {
  FormControl,
  FormHelperText,
  FormLabel,
  Text,
  useColorMode,
} from "@chakra-ui/react";
import Select, { SingleValue } from "react-select";
import React, { useEffect, useState } from "react";
import AudienceDomain from "entities/domain/audience";
import useAudiencesStore from "hooks/use-audiences-store";
import useMerchantStore from "hooks/use-merchant-store";
import AudiencesService from "services/audiences";
import { useAuth0 } from "@auth0/auth0-react";
import { getReactSelectStyles } from "util/methods";
import useCustomChakraTheme from "hooks/use-custom-chakra-theme";
import CampaignDomain, { CampaignStatus } from "entities/domain/campaign";

interface ExistingAudienceTabProps {
  isInitialLoadCompleted: boolean;
  audienceId: string | null;
  campaign: CampaignDomain;
  setCampaign: (c: CampaignDomain) => void;
  setAudienceId: (audienceId: string | null) => void;
}

interface SelectOption {
  value: string;
  label: string;
}

const ExistingAudienceTab = ({
  audienceId,
  isInitialLoadCompleted,
  campaign,
  setCampaign,
  setAudienceId,
}: ExistingAudienceTabProps) => {
  const auth0Context = useAuth0();
  const { colorMode } = useColorMode();
  const { colorScheme } = useCustomChakraTheme();
  const {
    audiences,
    loading: isFetching,
    createAudience,
    propagateAudienceUpdate,
  } = useAudiencesStore();
  const { merchant } = useMerchantStore();

  const [audienceCustomersCount, setAudienceCustomersCount] =
    useState<number>(0);
  const [showCustomersCount, setShowCustomersCount] = useState<boolean>(false);

  const createDefaultAudience = async (): Promise<void> => {
    try {
      const defaultAudience = new AudienceDomain(
        null,
        "All subscribed customers",
        merchant.id,
        0,
        []
      );

      await createAudience({
        name: defaultAudience.name.trim(),
        tags: defaultAudience.tags,
        merchantId: defaultAudience.merchantId,
      });
    } catch (e) {
      // eslint-disable-next-line
      console.error("Failed to create default audience");
    }
  };

  const audienceOptions: SelectOption[] = audiences.map(
    (a: AudienceDomain) => ({ label: a.name, value: a.id as string })
  );

  const [selectedAudienceValue, setSelectedAudienceValue] = useState<
    SingleValue<SelectOption>
  >(audienceOptions.find(({ value }) => value === audienceId) || null);

  const handleAudienceChange = async (e: SingleValue<SelectOption>) => {
    let foundAudience = e?.value
      ? audiences.find((a: AudienceDomain) => a.id === e.value)
      : null;

    setAudienceCustomersCount(0);

    if (foundAudience && foundAudience.count === 0) {
      try {
        foundAudience = await AudiencesService.getAudience(
          auth0Context,
          foundAudience.id as string
        );
        setShowCustomersCount(true);
      } catch (error) {
        // eslint-disable-next-line
        console.error("Failed to fetch a single audience", error);
        setShowCustomersCount(false);
      }
    }
    if (foundAudience?.id) {
      propagateAudienceUpdate(foundAudience);
    }
    setAudienceId(foundAudience?.id || null);
    setSelectedAudienceValue(e);
    setCampaign(
      Object.setPrototypeOf(
        { ...campaign, audienceId: foundAudience?.id || null },
        CampaignDomain.prototype
      )
    );
  };

  useEffect(() => {
    if (isFetching || !isInitialLoadCompleted) {
      return;
    }

    const hasDefaultAudience =
      audiences.length &&
      audiences.find(({ tags }: AudienceDomain) => {
        return tags.length === 0;
      });

    if (audiences.length === 0 || !hasDefaultAudience) {
      createDefaultAudience();
    }
  }, [audiences, isFetching]);

  useEffect(() => {
    if (audienceId !== selectedAudienceValue?.value) {
      const selectedAudienceOption =
        audienceOptions.find(({ value }) => value === audienceId) || null;

      handleAudienceChange(selectedAudienceOption);
    }
  }, [audienceId]);

  useEffect(() => {
    if (!audienceId || !audiences.length) {
      setShowCustomersCount(false);
      return;
    }

    const foundAudience = audiences.find(
      (a: AudienceDomain) => a.id === audienceId
    );
    setAudienceCustomersCount(foundAudience?.count || 0);
    setShowCustomersCount(true);
  }, [audienceId, audiences]);

  return (
    <FormControl mt={6}>
      <FormLabel>
        <Text fontWeight={700} fontSize="xl" textAlign="center" mr={0} mb={6}>
          Choose an audience
        </Text>
      </FormLabel>
      <Select
        placeholder="Select existing audience"
        onChange={handleAudienceChange}
        options={audienceOptions}
        value={selectedAudienceValue}
        styles={getReactSelectStyles(colorMode, colorScheme)}
        isDisabled={campaign.status === CampaignStatus.DONE}
      />
      {showCustomersCount ? (
        <FormHelperText>
          <Text color="gray.400" fontWeight={600}>
            {`${audienceCustomersCount} ${
              audienceCustomersCount === 1 ? "member" : "members"
            } in this audience`}
          </Text>
        </FormHelperText>
      ) : null}
    </FormControl>
  );
};

export default ExistingAudienceTab;
